import {
  CaretDownFilled,
  CaretUpFilled,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  message,
  Modal,
  notification,
  Select,
  Tooltip,
  Typography,
} from 'antd';
import { useWatch } from 'antd/lib/form/Form';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import useAPIQuery from '../../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../../api/useInfiniteAPIQuery';
import Form from '../../../components/Form';
import useDiscardChangesModal from '../../../hooks/useDiscardChangesModal';
import usePrefetchPages from '../../../hooks/usePrefetchPages';
import useTypedParams from '../../../hooks/useTypedParams';
import { SinapseScreeningQuestionsResult } from '../../../sinapse/api';
import { ACTION_GENERATE_SCREENING_QUESTIONS } from '../../../sinapse/constants/actions';
import { ScreeningQuestionsGenerator } from '../../../sinapse/generate_screening_questions';
import useAsyncSinapseResponse from '../../../sinapse/hooks/useAsyncSinapseResponse';
import { JobPostingId } from '../../../types';
import { isDescriptionEmpty } from '../PublicationForm/PublicationInfoForm';
import StagesFormLoadingContext from './StagesFormLoadingContext';

interface AlternativeFormValue {
  id?: number;
  description: string;
  is_correct: boolean;
}

interface QuestionFormValue {
  id?: number;
  type: 'CHECKBOX' | 'TEXT' | 'RADIO';
  alternatives?: AlternativeFormValue[];
  screening: boolean;
  description: string;
  required?: boolean;
}

export interface AdditionalQuestionsFormValues {
  questions: QuestionFormValue[];
}

interface AdditionalQuestionsFormProps {
  setIsDirty: ReturnType<typeof useDiscardChangesModal>[0];
}

const AdditionalQuestionsForm = ({ setIsDirty }: AdditionalQuestionsFormProps) => {
  const { t } = useTranslation();

  const config = useAPIQuery('/configs/tenant-configurations/');

  const enableCandidateAutomaticMovimentation = Boolean(
    config.data?.enableCandidateAutomaticMovimentation
  );

  const { id } = useTypedParams({ id: JobPostingId });

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const [form] = Form.useForm();

  const questionsValue = useWatch<AdditionalQuestionsFormValues['questions'] | undefined>(
    'questions',
    form
  );

  const questionsPages = useInfiniteAPIQuery(['/questions/', { params: { job_posting_id: id } }], {
    enabled: config.isSuccess,
  });

  const questions = usePrefetchPages(questionsPages, (data) => data.results);

  const recruitmentProcesses = useAPIQuery([
    '/recruitment-processes/',
    {
      params: {
        job_posting: id,
      },
    },
  ]);

  const disableAdditionalQuestions = (recruitmentProcesses.data?.count ?? 0) > 0;

  const [disabledInput, setIsDisabledInput] = useState<boolean>(false);

  const { responseStatus, processResult } =
    useAsyncSinapseResponse<SinapseScreeningQuestionsResult>({
      setIsDisabledInput,
      setResult: (result) => {
        if (!result.questions) {
          return notification.error({
            message: t('Erro ao gerar as perguntas de pré-requisito'),
            description: result.text,
          });
        }

        form.setFieldValue(
          'questions',
          result.questions.map(({ question, options }) => ({
            description: question,
            required: false,
            type: 'RADIO',
            screening: false,
            alternatives: options.map((option) => ({ description: option, is_correct: false })),
          }))
        );

        message.success(t('Perguntas geradas com sucesso'));
      },
      sourceId: id,
      action: ACTION_GENERATE_SCREENING_QUESTIONS,
      configs: {
        overwriteOnSuccess: true,
        queryKeyPath: ['sinapse', 'screeningQuestions', id],
      },
      enabled: config.data?.enableGenerateScreeningQuestions,
    });

  const confirmAdditionalQuestionsOverride = useCallback(
    () =>
      Modal.confirm({
        title: t('Sobrescrever as perguntas atuais?'),
        content: t(
          'Ao gerar as perguntas de pré-requisito, as perguntas atuais serão substituídas. Você tem certeza que deseja prosseguir?'
        ),
        onOk: () => processResult?.(),
        okText: t('Sim'),
        cancelText: t('Não'),
        okButtonProps: {
          icon: <CheckOutlined />,
        },
        cancelButtonProps: { icon: <CloseOutlined />, danger: true },
      }),
    [processResult, t]
  );

  const loadingAIResponse = responseStatus === 'pending' && disabledInput;

  const isJobPostingDescriptionEmpty = isDescriptionEmpty(jobPosting.data?.description);

  const disabled = disableAdditionalQuestions || loadingAIResponse;

  const isLoading =
    recruitmentProcesses.isLoading ||
    questionsPages.isLoading ||
    jobPosting.isLoading ||
    config.isLoading;

  const stagesFormLoadingContext = useContext(StagesFormLoadingContext);

  useEffect(() => {
    stagesFormLoadingContext?.setAdditionalQuestionsLoading(isLoading);

    return () => stagesFormLoadingContext?.setAdditionalQuestionsLoading(false);
  }, [stagesFormLoadingContext, isLoading]);

  const resetQuestionAlternativesIsCorrect = (name: number) => {
    form.setFieldValue(
      ['questions', name, 'alternatives'],
      questionsValue?.[name]?.alternatives?.map((alternative) => ({
        ...alternative,
        is_correct: false,
      })) ?? []
    );
  };

  return (
    <Form<AdditionalQuestionsFormValues>
      layout="vertical"
      form={form}
      name="additionalQuestionsForm"
      initialValues={{
        questions: questions.map((question) => ({
          id: question.id,
          type: question.type,
          alternatives: question.alternatives?.map((alternative) => ({
            id: alternative.id,
            description: alternative.description,
            is_correct: enableCandidateAutomaticMovimentation && alternative.is_correct,
          })),
          description: question.description,
          required: question.required,
          screening: enableCandidateAutomaticMovimentation && question.screening,
        })),
      }}
      onValuesChange={() => setIsDirty(form.isFieldsTouched())}
    >
      {disableAdditionalQuestions && (
        <>
          <Alert
            message={t(
              'Não é possível editar as perguntas de pré-requisito, pois já existem candidatos no processo seletivo dessa vaga'
            )}
            type="info"
            showIcon
          />
          <br />
        </>
      )}
      <Typography.Paragraph>
        <Trans>
          Faça até 5 perguntas de pré-requisito para validar os requisitos mínimos da vaga. A pessoa
          candidata irá respondê-las após o preenchimento do formulário de inscrição:
        </Trans>
      </Typography.Paragraph>
      {config.data?.enableGenerateScreeningQuestions && (
        <ScreeningQuestionsGenerator
          loading={loadingAIResponse}
          disabled={disabled || isJobPostingDescriptionEmpty}
          disabledMessage={
            isJobPostingDescriptionEmpty
              ? t('Preencha a descrição da vaga para gerar as perguntas')
              : ''
          }
          onGenerateClick={() => {
            setIsDirty(true);

            if (questionsValue && questionsValue.length > 0) {
              return confirmAdditionalQuestionsOverride();
            }

            processResult?.();
          }}
        />
      )}
      <Form.List name="questions">
        {(fields, { add, remove, move }) => (
          <>
            {fields.map(({ key, name: questionName }, idx) => (
              <div key={key}>
                <Form.Row>
                  <Form.Col flex={1}>
                    <Form.Item
                      name={[questionName, 'description']}
                      label={
                        <>
                          <Typography.Text>
                            <Trans>Pergunta</Trans> {questionName + 1}{' '}
                            <Button
                              type="text"
                              disabled={disabled}
                              size="small"
                              title={t('Mover Pergunta {{name}} para cima', {
                                name: questionName + 1,
                              })}
                              onClick={() => move(idx, idx - 1)}
                            >
                              <CaretUpFilled />
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              disabled={disabled}
                              title={t('Mover Pergunta {{name}} para baixo', {
                                name: questionName + 1,
                              })}
                              onClick={() => move(idx, idx + 1)}
                            >
                              <CaretDownFilled />
                            </Button>
                          </Typography.Text>
                        </>
                      }
                      rules={[
                        { required: true, message: t('Campo obrigatório') },
                        {
                          validator(_, value) {
                            if (!value || value.length <= 255) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(t('A pergunta não pode ultrapassar 255 caracteres'))
                            );
                          },
                        },
                      ]}
                    >
                      <Input disabled={disabled} />
                    </Form.Item>
                  </Form.Col>
                  <Form.Col span={5}>
                    <Form.Item
                      name={[questionName, 'type']}
                      label={t('Formato da pergunta')}
                      required
                      rules={[{ required: true }]}
                    >
                      <Select
                        disabled={disabled}
                        onChange={(value) => {
                          if (value === 'TEXT') {
                            return form.setFieldValue(
                              ['questions', questionName, 'alternatives'],
                              []
                            );
                          }

                          return resetQuestionAlternativesIsCorrect(questionName);
                        }}
                      >
                        <Select.Option value="CHECKBOX">
                          <Trans>Múltipla escolha</Trans>
                        </Select.Option>
                        <Select.Option value="RADIO">
                          <Trans>Única escolha</Trans>
                        </Select.Option>
                        <Select.Option
                          value="TEXT"
                          disabled={questionsValue?.[questionName].screening}
                        >
                          <Trans>Texto livre</Trans>
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Form.Col>
                  <Form.Col>
                    <Form.Item
                      name={[questionName, 'required']}
                      valuePropName="checked"
                      label={t('Tipo de pergunta')}
                    >
                      <Checkbox disabled={disabled || questionsValue?.[questionName].screening}>
                        <Trans>Obrigatória</Trans>{' '}
                        <Tooltip
                          title={t(
                            'O candidato não consegue enviar a candidatura sem responder esta pergunta.'
                          )}
                        >
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </Checkbox>
                    </Form.Item>
                  </Form.Col>
                  {enableCandidateAutomaticMovimentation && (
                    <Form.Col>
                      <Form.Item
                        name={[questionName, 'screening']}
                        valuePropName="checked"
                        label={<></>}
                      >
                        <Checkbox
                          disabled={disabled || questionsValue?.[questionName].type === 'TEXT'}
                          onChange={() => {
                            form.setFieldValue(['questions', questionName, 'required'], true);
                            resetQuestionAlternativesIsCorrect(questionName);
                          }}
                        >
                          <Trans>Classificatória</Trans>{' '}
                          <Tooltip
                            title={t(
                              'Defina as respostas corretas. Respostas abertas não são aceitas. Candidatos que atenderem aos critérios serão movidos automaticamente para a etapa triados.'
                            )}
                          >
                            <QuestionCircleOutlined />
                          </Tooltip>
                        </Checkbox>
                      </Form.Item>
                    </Form.Col>
                  )}
                  <Form.Col>
                    <Form.Item label={<></>}>
                      <Button
                        disabled={disabled}
                        size="small"
                        type="text"
                        title={t('Remover Pergunta {{name}}', { name: questionName + 1 })}
                        onClick={() => remove(questionName)}
                      >
                        <DeleteOutlined />
                      </Button>
                    </Form.Item>
                  </Form.Col>
                </Form.Row>
                <>
                  {['RADIO', 'CHECKBOX'].includes(questionsValue?.[idx]?.type ?? '') && (
                    <Form.List
                      name={[questionName, 'alternatives']}
                      rules={[
                        {
                          validator: (_, value: AlternativeFormValue[]) => {
                            if (!value || value.length < 2) {
                              return Promise.reject(
                                new Error(t('A pergunta precisa ter no mínimo duas alternativas.'))
                              );
                            }

                            if (
                              questionsValue?.[questionName].screening &&
                              value.every(({ is_correct }) => !is_correct)
                            ) {
                              return Promise.reject(
                                new Error(t('Defina a(s) resposta(s) correta(s)'))
                              );
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      {(fields, { add, remove }, { errors }) => (
                        <Form.Item>
                          <Form.Row style={{ marginBottom: 8 }}>
                            <Form.Col span={13}>
                              <Trans>Alternativas</Trans>
                            </Form.Col>
                            {enableCandidateAutomaticMovimentation &&
                              questionsValue?.[questionName].screening && (
                                <Form.Col span={11}>
                                  <Trans>Resposta desejada</Trans>
                                </Form.Col>
                              )}
                          </Form.Row>
                          {fields.map(({ key, name: alternativeName }) => (
                            <Form.Row key={`alternatives${key}`}>
                              <Form.Col span={12}>
                                <Form.Item
                                  name={[alternativeName, 'description']}
                                  rules={[
                                    { required: true, message: t('Campo obrigatório') },
                                    {
                                      validator(_, value) {
                                        if (!value || value.length <= 255) {
                                          return Promise.resolve();
                                        }
                                        return Promise.reject(
                                          new Error(
                                            t('A alternativa não pode ultrapassar 255 caracteres')
                                          )
                                        );
                                      },
                                    },
                                  ]}
                                >
                                  <Input disabled={disabled} />
                                </Form.Item>
                              </Form.Col>
                              <Form.Col span={1}>
                                <Form.Item>
                                  <Button
                                    size="small"
                                    disabled={disabled}
                                    type="text"
                                    onClick={() => remove(alternativeName)}
                                  >
                                    <DeleteOutlined />
                                  </Button>
                                </Form.Item>
                              </Form.Col>
                              {enableCandidateAutomaticMovimentation &&
                                questionsValue?.[questionName].screening && (
                                  <Form.Col span={1}>
                                    <Form.Item
                                      name={[alternativeName, 'is_correct']}
                                      valuePropName="checked"
                                    >
                                      <Checkbox
                                        disabled={
                                          disabled ||
                                          (questionsValue[questionName].type === 'RADIO' &&
                                            !questionsValue[questionName].alternatives?.[
                                              alternativeName
                                            ].is_correct &&
                                            Boolean(
                                              questionsValue[questionName].alternatives?.find(
                                                ({ is_correct }) => is_correct
                                              )
                                            ))
                                        }
                                        onChange={() => {
                                          form.validateFields();
                                        }}
                                      />
                                    </Form.Item>
                                  </Form.Col>
                                )}
                            </Form.Row>
                          ))}
                          <Form.ErrorList errors={errors} />
                          <Button
                            type="link"
                            disabled={disabled}
                            onClick={() => add({ description: '', is_correct: false })}
                          >
                            <Trans>Adicionar alternativa</Trans>
                          </Button>
                        </Form.Item>
                      )}
                    </Form.List>
                  )}
                </>
              </div>
            ))}
            <Button
              icon={<PlusOutlined />}
              disabled={fields.length >= 5 || disabled}
              onClick={() =>
                add({
                  description: '',
                  type: 'TEXT',
                  required: false,
                  screening: false,
                  alternatives: [],
                })
              }
            >
              {' '}
              <Trans>Adicionar pergunta</Trans>
            </Button>
          </>
        )}
      </Form.List>
    </Form>
  );
};

export default AdditionalQuestionsForm;
